<template>
    <!-- Rendered INSIDE the timeline (TopProcessStepper) card, below the CTA strip.
         Styled as light, borderless rows so it reads as part of the timeline rather
         than a separate mentoring-style card. -->
    <div
        v-if="showMentee || showMentor"
        class="mt-4 flex flex-col gap-3 border-t border-gray-100 dark:border-default pt-4"
    >
        <!-- Mentee row — required participant contribution (1 % of gross salary) -->
        <template v-if="showMentee">
            <!-- Paid (any step) -->
            <div v-if="menteePaid" class="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                <Icon name="heroicons:check-circle" class="h-5 w-5 shrink-0 text-green-600 dark:text-green-300" />
                <span><strong>Účastnícky príspevok Mentee</strong> — uhradené, ďakujeme!</span>
            </div>
            <!-- Prominent (step 4) -->
            <div v-else-if="variant === 'prominent'" class="flex items-start justify-between gap-3">
                <div class="flex min-w-0 items-start gap-2.5">
                    <Icon name="heroicons:banknotes" class="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-300" />
                    <div class="min-w-0">
                        <p class="text-sm font-semibold text-gray-900 dark:text-txt-headings">Účastnícky príspevok Mentee</p>
                        <p class="mt-0.5 text-xs text-gray-500 dark:text-txt-body">
                            Súčasťou účasti v programe je príspevok
                            <strong class="font-semibold text-gray-700 dark:text-txt-body">1 % z hrubej mzdy</strong>.
                        </p>
                        <p class="mt-1 inline-flex items-center gap-1 text-xs text-orange-600 dark:text-orange-300">
                            <Icon name="heroicons:exclamation-triangle" class="h-3.5 w-3.5" />
                            Stav: neuhradené
                        </p>
                    </div>
                </div>
                <NuxtLink
                    to="/mentoring-network-pre-svetove-slovensko/podpor"
                    class="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#15803d] px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#166534]"
                >
                    Prispieť
                    <Icon name="heroicons:arrow-right" class="h-4 w-4" />
                </NuxtLink>
            </div>
            <!-- Muted (steps 1-3) -->
            <div v-else class="flex items-center justify-between gap-3">
                <div class="flex min-w-0 items-center gap-2.5">
                    <Icon name="heroicons:banknotes" class="h-4 w-4 shrink-0 text-gray-400 dark:text-txt-body" />
                    <span class="text-xs text-gray-500 dark:text-txt-body">
                        <strong class="font-semibold text-gray-700 dark:text-txt-body">Účastnícky príspevok Mentee</strong>
                        — 1 % z hrubej mzdy ročne
                    </span>
                </div>
                <NuxtLink
                    to="/mentoring-network-pre-svetove-slovensko/podpor"
                    class="shrink-0 text-sm font-medium text-[#49bd57] hover:underline"
                >
                    Prispieť →
                </NuxtLink>
            </div>
        </template>

        <!-- Mentor row — voluntary financial contribution (1 000 – 3 000 €) -->
        <template v-if="showMentor">
            <!-- Paid (any step) -->
            <div v-if="mentorPaid" class="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                <Icon name="heroicons:check-circle" class="h-5 w-5 shrink-0 text-green-600 dark:text-green-300" />
                <span><strong>Finančný príspevok Mentora</strong> — uhradené, ďakujeme!</span>
            </div>
            <!-- Prominent (step 4) -->
            <div v-else-if="variant === 'prominent'" class="flex items-start justify-between gap-3">
                <div class="flex min-w-0 items-start gap-2.5">
                    <Icon name="heroicons:banknotes" class="mt-0.5 h-5 w-5 shrink-0 text-purple-600 dark:text-purple-300" />
                    <div class="min-w-0">
                        <p class="text-sm font-semibold text-gray-900 dark:text-txt-headings">Finančný príspevok Mentora</p>
                        <p class="mt-0.5 text-xs text-gray-500 dark:text-txt-body">
                            <strong class="font-semibold text-gray-700 dark:text-txt-body">1 000 – 3 000 €</strong> ročne. Ak je to v
                            tvojich možnostiach, veľmi oceníme tvoj príspevok — pomôžeš nám udržať program v chode.
                            Nie je povinný.
                        </p>
                    </div>
                </div>
                <NuxtLink
                    to="/mentoring-network-pre-svetove-slovensko/podpor"
                    class="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
                >
                    Prispieť
                    <Icon name="heroicons:arrow-right" class="h-4 w-4" />
                </NuxtLink>
            </div>
            <!-- Muted (steps 1-3) -->
            <div v-else class="flex items-center justify-between gap-3">
                <div class="flex min-w-0 items-center gap-2.5">
                    <Icon name="heroicons:banknotes" class="h-4 w-4 shrink-0 text-gray-400 dark:text-txt-body" />
                    <span class="text-xs text-gray-500 dark:text-txt-body">
                        <strong class="font-semibold text-gray-700 dark:text-txt-body">Finančný príspevok Mentora</strong>
                        — 1 000 – 3 000 € ročne
                    </span>
                </div>
                <NuxtLink
                    to="/mentoring-network-pre-svetove-slovensko/podpor"
                    class="shrink-0 text-sm font-medium text-purple-600 dark:text-purple-300 hover:underline"
                >
                    Prispieť →
                </NuxtLink>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { UserProfile } from '#shared/types/User'

withDefaults(defineProps<{
    variant?: 'muted' | 'prominent'
}>(), {
    variant: 'muted'
})

const store = useApplicationStore()
const userProfile = computed<UserProfile | null>(() => store.userProfile as UserProfile | null)

// Show role-specific row only for the roles the user is TOP-approved in.
// If the user is both mentor and mentee, both rows render (mentee first).
const showMentee = computed(() => Boolean(userProfile.value?.menteeTopApproved))
const showMentor = computed(() => Boolean(userProfile.value?.mentorTopApproved))

const menteePaid = computed(() => Boolean(userProfile.value?.topMenteePaymentCompleted))
const mentorPaid = computed(() => Boolean(userProfile.value?.topMentorPaymentCompleted))
</script>
